import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { aws, setActor } from '../lib/awsdb';
import { storage } from '../lib/awsStorage';
import { authWrite } from '../lib/authWrite';
import { formatMoney } from '../lib/money';
import { logActivity } from '../lib/activityLog';

function today() { return new Date().toISOString().slice(0, 10); }

export default function QuickPayPopover({ invoice, outstanding, session, paymentMethods = [], onClose, onSaved }) {
  const [amount, setAmount] = useState(outstanding > 0 ? outstanding.toFixed(2) : '');
  const [paidAt, setPaidAt] = useState(today());
  const [method, setMethod] = useState('');
  const [newMethodMode, setNewMethodMode] = useState(false);
  const [newMethodName, setNewMethodName] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const overpayWarning = useMemo(() => {
    const a = parseFloat(amount);
    if (!a || !(outstanding >= 0)) return null;
    if (a > outstanding + 0.01) return `Amount exceeds outstanding (${formatMoney(outstanding)}) by ${formatMoney(a - outstanding)}.`;
    return null;
  }, [amount, outstanding]);

  function handleMethodChange(val) {
    if (val === '__new__') {
      setNewMethodMode(true);
      setMethod('');
    } else {
      setMethod(val);
      setNewMethodMode(false);
    }
  }

  async function addNewMethod() {
    const name = newMethodName.trim();
    if (!name) return;
    try {
      await authWrite(session, 'payment_methods', 'insert', { name });
    } catch (e) {
      setError(e.message); return;
    }
    setMethod(name);
    setNewMethodMode(false);
    setNewMethodName('');
  }

  async function save() {
    setError('');
    const a = parseFloat(amount);
    if (!a || a <= 0) return setError('Amount required and must be > 0.');
    if (!method) return setError('Payment method is required.');
    setSaving(true);
    await setActor(session);
    let inserted;
    try {
      const rows = await authWrite(session, 'po_invoice_payments', 'insert', {
        invoice_id: invoice.id,
        paid_at: paidAt || today(),
        amount: a,
        method: method || null,
        created_by: session.code,
      });
      inserted = Array.isArray(rows) ? rows[0] : null;
    } catch (e) {
      setSaving(false);
      setError(e.message); return;
    }
    setSaving(false);
    if (!inserted) { setError('No row returned'); return; }
    let proofPath = null;
    if (proofFile && inserted) {
      const safeName = proofFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const key = `${inserted.id}/${Date.now()}-${safeName}`;
      const { error: upErr, data: upData } = await storage.from('payment-proofs').upload(key, proofFile, { contentType: proofFile.type, upsert: false });
      if (!upErr) {
        proofPath = upData?.path || key;
        await authWrite(session, 'po_invoice_payments', 'update',
          { proof_storage_path: proofPath },
          { column: 'id', value: inserted.id });
      } else {
        proofPath = null;
      }
    }
    const refNum = `PAY-${inserted.id.slice(0, 6).toUpperCase()}`;
    await logActivity(invoice.id, session, {
      action_type: 'payment_logged',
      after_value: formatMoney(a),
      notes: `detail:${JSON.stringify({
        ref: refNum, amount: a, method, date: paidAt || today(),
        proof_file: proofFile?.name || null,
        proof_path: proofPath, proof_bucket: proofPath ? 'payment-proofs' : null
      })}`
    });
    // Auto-mark as paid if outstanding is now zero
    const newOutstanding = outstanding - a;
    if (newOutstanding <= 0.01 && Number(invoice.total) > 0 && invoice.status !== 'paid') {
      await authWrite(session, 'po_invoices', 'update', { status: 'paid' }, { column: 'id', value: invoice.id });
      await logActivity(invoice.id, session, { action_type: 'status_change', before_value: invoice.status, after_value: 'paid' });
    }
    onSaved?.();
    onClose();
  }

  return createPortal(
    <>
      <div className="quickpay-backdrop" onClick={onClose} />
      <div className="quickpay-modal" role="dialog" aria-label="Log payment">
        <div className="quickpay-title">
          <span>Log payment</span>
          <span className="quickpay-invoice">{invoice.invoice_number}</span>
        </div>
        <div className="quickpay-row">
          <label>Date</label>
          <input type="date" value={paidAt} onChange={e => setPaidAt(e.target.value)} />
        </div>
        <div className="quickpay-row">
          <label>Amount <span className="muted-xs">(outstanding {formatMoney(outstanding)})</span></label>
          <input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} autoFocus />
        </div>
        <div className="quickpay-row">
          <label>Method</label>
          <select value={newMethodMode ? '__new__' : method} onChange={e => handleMethodChange(e.target.value)}>
            <option value="">Select method...</option>
            {paymentMethods.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
            <option value="__new__">+ New method</option>
          </select>
          {newMethodMode && (
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <input type="text" placeholder="New method name" value={newMethodName} onChange={e => setNewMethodName(e.target.value)} style={{ flex: 1 }} />
              <button type="button" className="btn btn-primary btn-sm" onClick={addNewMethod}>Add</button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setNewMethodMode(false); setNewMethodName(''); }}>Cancel</button>
            </div>
          )}
        </div>
        <div className="quickpay-row">
          <label>Proof <span className="muted-xs">(optional)</span></label>
          <input type="file" accept="application/pdf,image/png,image/jpeg,application/rtf,text/rtf,.rtf" onChange={e => setProofFile(e.target.files?.[0] || null)} style={{ fontSize: 12 }} />
        </div>
        {overpayWarning && <div className="quickpay-warn">{overpayWarning}</div>}
        {error && <div className="quickpay-err">{error}</div>}
        <div className="quickpay-actions">
          <button className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save payment'}</button>
        </div>
      </div>
    </>,
    document.body
  );
}
